import React, { useMemo } from 'react';
import { Lock } from 'lucide-react';
import { BasketIcon } from '../BasketIcon';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

const RIPE_LINE = '#C9982E';
const HUB_R = 10; // % radius for spoke placement
const SPOKE_R = 38;

/**
 * Radial hub-and-spoke: hub = basket, spokes = topics.
 * angle_i = (2π · i / n) − π/2
 * x/y as % of a square container.
 */
const BasketHubGraph = ({
  basket,
  selectedTopicId,
  matchedTopicId,
  onSelectTopic,
}) => {
  const { isPremium, isLoading } = usePremium();
  const topics = useMemo(
    () => basket?.topics || [],
    [basket]
  );

  const spokePositions = useMemo(() => {
    const n = topics.length;
    if (n === 0) return [];

    return topics.map((topic, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      const x = 50 + SPOKE_R * Math.cos(angle);
      const y = 50 + SPOKE_R * Math.sin(angle);
      return { topic, x, y, angle };
    });
  }, [topics]);

  if (!basket) return null;

  const ripeCount = topics.filter((topic) => topic.is_ripe).length;

  return (
    <div
      className="group/hub relative w-full max-w-md mx-auto aspect-square"
      role="img"
      aria-label={`Basket ${basket.name} with ${topics.length} topics`}
    >
      <p
        className="absolute top-0 left-0 z-30 text-xs text-neutral-500 transition-opacity opacity-100 md:opacity-0 md:group-hover/hub:opacity-100 md:group-focus-within/hub:opacity-100"
        aria-label={`${ripeCount} of ${topics.length} ripe`}
      >
        {ripeCount}/{topics.length} ripe
      </p>

      {/* Spoke lines */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        {spokePositions.map(({ topic, x, y }) => (
          <line
            key={`line-${topic.id}`}
            x1="50"
            y1="50"
            x2={x}
            y2={y}
            stroke={topic.is_ripe ? RIPE_LINE : '#A3A3A3'}
            strokeWidth="0.6"
            strokeDasharray={topic.is_ripe ? undefined : '1.5 1.5'}
            strokeLinecap="round"
          />
        ))}
      </svg>

      {/* Hub — basket, centered */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center justify-center w-24 h-24 rounded-full bg-white border-2 border-neutral-900 shadow-sm px-2"
        style={{ width: `${HUB_R * 2}%`, height: `${HUB_R * 2}%`, minWidth: '5.5rem', minHeight: '5.5rem' }}
      >
        <div className="scale-90 [&_svg]:mr-0">
          <BasketIcon />
        </div>
        <p className="text-[11px] sm:text-xs font-semibold text-neutral-900 text-center leading-tight line-clamp-2 mt-0.5">
          {basket.name}
        </p>
      </div>

      {/* Topic spokes */}
      {spokePositions.map(({ topic, x, y }) => {
        const isSelected = topic.id === selectedTopicId;
        const isMatch = topic.id === matchedTopicId;
        const tagColor = topic.tag?.color || topic.tags?.color;
        const topicLocked = isLocked(topic, isPremium, isLoading);
        const relationText = topic.relation_description?.trim() || '';
        // Skip the selected spoke — its relation is already in the inspect panel.
        const showRelationTooltip =
          Boolean(relationText) && !topicLocked && topic.id !== selectedTopicId;
        // Point the tooltip toward the hub so it stays inside the square.
        const tooltipAbove = y > 50;

        return (
          <button
            key={topic.id}
            type="button"
            onClick={() => onSelectTopic(topic)}
            className={`
              group/spoke absolute z-20 -translate-x-1/2 -translate-y-1/2
              w-20 sm:w-24 px-2 py-2 rounded-xl border-2 bg-white
              text-center transition-shadow touch-manipulation
              focus:outline-none focus:ring-2 focus:ring-cranberry-700
              ${isSelected ? 'border-neutral-900 shadow-md' : 'border-neutral-300 shadow-sm hover:border-neutral-500'}
              ${isMatch ? 'ring-2 ring-offset-2 ring-[#C9982E]' : ''}
            `}
            style={{ left: `${x}%`, top: `${y}%` }}
            aria-label={
              topicLocked
                ? `${topic.name} (Premium)`
                : showRelationTooltip
                  ? `${topic.name}. ${relationText}`
                  : topic.name
            }
            aria-pressed={isSelected}
          >
            {topic.is_anchor && (
              <span className="absolute -top-2 -left-2 text-[9px] font-bold text-butter-900 bg-butter-500 border-2 border-white rounded-full px-1.5 leading-tight">
                anchor
              </span>
            )}
            {tagColor && (
              <span
                className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white pointer-events-none"
                style={{ backgroundColor: tagColor }}
                aria-hidden="true"
              />
            )}
            {topicLocked && (
              <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-neutral-100 border border-neutral-300 text-neutral-500">
                <Lock size={10} aria-hidden="true" />
              </span>
            )}
            <span className="block text-[11px] sm:text-xs font-medium text-neutral-900 leading-tight line-clamp-2">
              {topic.name}
            </span>
            {showRelationTooltip && (
              <span
                role="tooltip"
                className={`
                  pointer-events-none absolute left-1/2 z-40 w-40 -translate-x-1/2
                  rounded-lg bg-neutral-900 px-2.5 py-2 text-left text-[11px]
                  font-normal leading-snug text-white shadow-lg
                  invisible opacity-0 transition-opacity
                  group-hover/spoke:visible group-hover/spoke:opacity-100
                  group-focus-visible/spoke:visible group-focus-visible/spoke:opacity-100
                  ${tooltipAbove ? 'bottom-full mb-2' : 'top-full mt-2'}
                `}
              >
                {relationText}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default BasketHubGraph;
